import React, {useContext, useEffect, useState} from 'react';

import {v4} from 'uuid';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {
  convertLocationParamsToFetchParams,
  DOCUMENT_TAG,
  useDocuments,
} from 'app/documents/api';
import {
  confirmOnLeaveEnhancer,
  type ConfirmOnLeaveProps,
} from 'app/general/confirmOnLeaveEnhancer';
import {DocumentAlertBox} from 'app/general/structures/document-alerts/DocumentAlertBox';
import StateButton from 'app/general/widgets/StateButton';
import {SuggestionChat} from 'app/openai/components/SuggestionChat';
import {extractSuggestionsOfType} from 'app/openai/utils';
import {sweetAPI} from 'app/store/sweet-api';
import {useChatMessageSubscription} from 'app/subscriptions/hooks';
import {HOME_WELCOME_INVITATIONS} from 'app/urls';
import {gt, ngt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {WebsocketContext} from 'app/ws/context';

import {PolicyDefinition, ProcedureDefinition} from './Definitions';
import {FullDocItem} from './FullDocItem';
import {NewDocumentOnlineGeneration} from './NewDocumentOnlineGeneration';

import type {SuggestionItem} from 'app/openai/types';
import type {WithRouterProps} from 'react-router';

const DEFAULT_PROCEDURE_SUGGESTED_TITLES = [
  'Secure Payment Processing',
  'Product Delivery Tracking',
  'Customer Service Response',
];

const DEFAULT_POLICY_SUGGESTED_TITLES = [
  'Privacy Protection',
  'Return and Refund',
  'Product Information Accuracy',
];

const enhance = confirmOnLeaveEnhancer(() =>
  gt('Your documents are being generated. Are you sure you want to leave?')
);

type Props = WithRouterProps &
  ConfirmOnLeaveProps & {
    onNext: (location: string) => unknown;
  };
const BaseSuggestions = ({
  onNext,
  shouldConfirmOnLeave,
  clearConfirmations,
}: Props) => {
  const [threadId] = useState(v4());

  const actions = useAsyncActions({
    invalidateTags: sweetAPI.util.invalidateTags,
  });

  const [generatedDocumentsCount, setGeneratedDocumentsCount] = useState(0);

  useChatMessageSubscription((data) => {
    if (data.thread_id != null && data.thread_id !== threadId) {
      if (data.type === 'begin_stream') {
        setGeneratedDocumentsCount((currentCount) => currentCount + 1);
        shouldConfirmOnLeave(data.thread_id, true);
      } else if (data.type === 'end_stream') {
        setGeneratedDocumentsCount((currentCount) => {
          if (currentCount - 1 < 0) {
            return 0;
          } else {
            return currentCount - 1;
          }
        });
        // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
        actions.invalidateTags([{type: DOCUMENT_TAG, id: 'list'}]);
        shouldConfirmOnLeave(data.thread_id, false);
      }
    }
  }, []);

  const {isWSOpen, isWSClosing} = useContext(WebsocketContext);

  useEffect(() => {
    if (isWSClosing) {
      clearConfirmations();
      setGeneratedDocumentsCount(0);
    }
  }, [isWSClosing]);

  const [procedures, setProcedures] = useState<Array<string>>(
    DEFAULT_PROCEDURE_SUGGESTED_TITLES
  );
  const [policies, setPolicies] = useState<Array<string>>(
    DEFAULT_POLICY_SUGGESTED_TITLES
  );

  const [suggestions, setSuggestions] = useState<null | Array<SuggestionItem>>(
    null
  );

  useEffect(() => {
    if (suggestions != null) {
      setProcedures(extractSuggestionsOfType(suggestions, 'procedure'));
      setPolicies(extractSuggestionsOfType(suggestions, 'policy'));
    }
  }, [suggestions]);

  const {data: aiDocs} = useDocuments(
    convertLocationParamsToFetchParams({
      folder: '',
      filter: 'is_ai_generated',
      ordering: '-created_at',
      page_size: '10',
    })
  );

  return (
    <>
      <div className="row">
        <div className="col">
          <div className="lead">
            {gt(
              "Let's kick start your documentation with the power of Artificial Intelligence."
            )}{' '}
            {gt(
              'Sit back and let the AI suggest (and write) %(procedure)s and %(policy)s that would be useful to a business like yours.',
              {procedure: <ProcedureDefinition />, policy: <PolicyDefinition />}
            )}
            {!isWSOpen ? (
              <div className="m-2">
                <DocumentAlertBox
                  type={'warning'}
                  icon="fa-robot"
                  title={gt('Connection with SweetAI lost.')}
                  content={gt(
                    'Feel free to skip this step. Documents can be' +
                      ' generated with SweetAI later on.'
                  )}
                />
              </div>
            ) : null}
            <SuggestionChat
              initialThreadId={threadId}
              setResults={setSuggestions}
              disabled={generatedDocumentsCount !== 0}
              shouldUpdateAccount={true}
            >
              <ul className="list-group mt-4">
                {procedures.map((doc, i) => (
                  <NewDocumentOnlineGeneration
                    key={`${doc}-${i}`}
                    documentName={doc}
                    content_type="procedure"
                  />
                ))}
                {policies.map((doc, i) => (
                  <NewDocumentOnlineGeneration
                    key={`${doc}-${i}`}
                    documentName={doc}
                    content_type="policy"
                  />
                ))}
              </ul>
            </SuggestionChat>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col">
          <ul className="list-group mt-4">
            {aiDocs != null &&
              aiDocs.results.map((doc) => (
                <FullDocItem key={doc.url} object={doc} />
              ))}
          </ul>
        </div>
      </div>

      <div className="row mt-4 justify-content-end">
        <div className="col-sm-auto">
          <LazyTooltip
            tooltip={gt('Start using SweetProcess with an empty account.')}
          >
            <button
              className="btn text-secondary btn-link mr-2"
              onClick={() => onNext(HOME_WELCOME_INVITATIONS)}
            >
              {gt('Skip')}
            </button>
          </LazyTooltip>
          {generatedDocumentsCount > 0 ? (
            <LazyTooltip
              tooltip={gt(
                'Your documents are being prepared. Wait for generation' +
                  ' process to finish.'
              )}
            >
              <div className="btn btn-primary disabled">
                {ngt(
                  'Add Document',
                  'Add Documents',
                  (aiDocs?.count || 0) + generatedDocumentsCount
                )}
              </div>
            </LazyTooltip>
          ) : (
            <StateButton
              disabled={aiDocs?.count === 0}
              busyText={gt('Setting up account...')}
              className="btn btn-primary"
              onClick={() => {
                onNext(HOME_WELCOME_INVITATIONS);
              }}
            >
              {ngt('Add Document', 'Add Documents', aiDocs?.count || 0)}
            </StateButton>
          )}
        </div>
      </div>
    </>
  );
};

export const Suggestions = enhance<Props>(BaseSuggestions);
